import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { Category } from "@root/utils/entities";
import { useState, useMemo, useEffect } from "react";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ExpandMore,
  ExpandLess,
  Clear,
} from "@mui/icons-material";
import { eventService } from "@root/services/api/eventService";

type CategorySelectDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (selectedCategories: string[]) => void;
  categories: Category[];
  selectedCategories: string[];
  title?: string;
};

export const CategorySelectDialog = ({
  open,
  onClose,
  onSave,
  categories,
  selectedCategories: initialSelected,
  title = "Select Categories",
}: CategorySelectDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialSelected || []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [searchResults, setSearchResults] = useState<Category[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [useSearchResults, setUseSearchResults] = useState(false);

  // Flatten categories with subcategories for normal mode
  const flattenedCategories = useMemo(() => {
    if (useSearchResults) return searchResults;

    const flattened: Category[] = [];
    categories.forEach((cat) => {
      if (cat.subcategories && cat.subcategories.length > 0) {
        // Add parent category (without subcategories array)
        const parentCat: Category = {
          ...cat,
          subcategories: undefined,
        };
        flattened.push(parentCat);
        // Add subcategories with parent reference
        cat.subcategories.forEach((subcat) => {
          flattened.push({
            ...subcat,
            parentCategory: parentCat,
            parentCategoryId: parentCat.id,
          });
        });
      } else if (cat.parentCategoryId) {
        // This is a subcategory at root level, find its parent
        const parentCat = categories.find((c) => c.id === cat.parentCategoryId);
        if (parentCat) {
          flattened.push({
            ...cat,
            parentCategory: parentCat,
          });
        } else {
          flattened.push(cat);
        }
      } else {
        // Standalone category (no parent, no subcategories)
        flattened.push(cat);
      }
    });
    return flattened;
  }, [categories, useSearchResults, searchResults]);

  // Build a comprehensive map of ALL categories (including nested) for validation
  const allCategoriesMap = useMemo(() => {
    const map = new Map<string, Category>();
    const flattenAllCategories = (cats: Category[]) => {
      cats.forEach((cat) => {
        if (cat.id) {
          map.set(cat.id, cat);
        }
        if (cat.subcategories) {
          cat.subcategories.forEach((subcat) => {
            if (subcat.id) {
              map.set(subcat.id, {
                ...subcat,
                parentCategory: cat,
                parentCategoryId: cat.id,
              });
            }
          });
          flattenAllCategories(cat.subcategories);
        }
      });
    };
    flattenAllCategories(categories);
    return map;
  }, [categories]);

  // Keep local state in sync with prop
  // Now supports both parent categories and subcategories
  useEffect(() => {
    if (!open) return; // Don't update when dialog is closed

    // Accept all valid categories (both parent and subcategories)
    const filtered = (initialSelected || []).filter((id) => {
      return allCategoriesMap.has(id);
    });

    // Only update if the filtered list is actually different from current selection
    // This prevents clearing selections when searching
    setSelectedCategories((prev) => {
      const prevSet = new Set(prev);
      const filteredSet = new Set(filtered);

      // Check if sets are different
      if (prevSet.size !== filteredSet.size) {
        return filtered;
      }
      for (const id of prevSet) {
        if (!filteredSet.has(id)) {
          return filtered;
        }
      }
      // If initialSelected changed from parent, update to match
      // Otherwise, preserve current selection (important for search mode)
      const initialSet = new Set(initialSelected || []);
      if (prevSet.size !== initialSet.size) {
        return filtered;
      }
      for (const id of prevSet) {
        if (!initialSet.has(id)) {
          return filtered;
        }
      }
      return prev; // Keep previous selection if nothing changed from parent
    });
  }, [initialSelected, open, allCategoriesMap]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSearchResults([]);
      setUseSearchResults(false);
    }
  }, [open]);

  // Search categories when search term changes
  useEffect(() => {
    const searchCategories = async () => {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setUseSearchResults(false);
        return;
      }

      setIsSearching(true);
      setUseSearchResults(true);
      try {
        const response = await eventService.searchCategories(searchTerm);
        if (response.isSuccess && response.data?.categories) {
          // Search API returns subcategories (with parentCategoryId)
          // We need to group them by parent category and also include parent categories
          const searchResultsWithParents: Category[] = [];

          // Build a map of all categories from the base categories prop to find parents
          const categoryMap = new Map<string, Category>();
          const flattenAllCategories = (cats: Category[]) => {
            cats.forEach((cat) => {
              if (cat.id) {
                categoryMap.set(cat.id, cat);
              }
              if (cat.subcategories) {
                flattenAllCategories(cat.subcategories);
              }
            });
          };
          flattenAllCategories(categories);

          // Group subcategories by parent
          const parentMap = new Map<string, Category[]>();
          response.data.categories.forEach((subcategory) => {
            if (subcategory.parentCategoryId) {
              const parent = categoryMap.get(subcategory.parentCategoryId);
              if (parent) {
                if (!parentMap.has(subcategory.parentCategoryId)) {
                  parentMap.set(subcategory.parentCategoryId, []);
                }
                parentMap.get(subcategory.parentCategoryId)!.push({
                  ...subcategory,
                  parentCategory: parent,
                  parentCategoryId: parent.id,
                });
              }
            }
          });

          // Add parent categories with their subcategories
          parentMap.forEach((subcats, parentId) => {
            const parent = categoryMap.get(parentId);
            if (parent) {
              // Add parent category with its subcategories
              searchResultsWithParents.push({
                ...parent,
                subcategories: subcats,
              });
            }
          });

          setSearchResults(searchResultsWithParents);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Error searching categories:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(() => {
      searchCategories();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, categories]);

  // Helper function to get all subcategory IDs for a parent category
  const getSubcategoryIds = (parentId: string): string[] => {
    const parent = allCategoriesMap.get(parentId);
    if (!parent) return [];

    const subIds: string[] = [];
    const collectSubIds = (cat: Category) => {
      if (cat.subcategories) {
        cat.subcategories.forEach((subcat) => {
          if (subcat.id) {
            subIds.push(subcat.id);
            collectSubIds(subcat); // Handle nested subcategories
          }
        });
      }
    };
    collectSubIds(parent);
    return subIds;
  };

  // Helper function to check if all subcategories of a parent are selected
  const areAllSubcategoriesSelected = (parentId: string): boolean => {
    const subIds = getSubcategoryIds(parentId);
    if (subIds.length === 0) return false;
    return subIds.every((id) => selectedCategories.includes(id));
  };

  const handleToggle = (categoryId: string, isParentCategory: boolean) => {
    const category = allCategoriesMap.get(categoryId);
    if (!category) return;

    const currentIndex = selectedCategories.indexOf(categoryId);
    const newSelected = [...selectedCategories];

    if (isParentCategory) {
      // Toggle parent category - include/exclude all subcategories
      const subIds = getSubcategoryIds(categoryId);

      if (currentIndex === -1) {
        // Add parent category
        newSelected.push(categoryId);
        // Add all subcategories
        subIds.forEach((subId) => {
          if (!newSelected.includes(subId)) {
            newSelected.push(subId);
          }
        });
      } else {
        // Remove parent category
        newSelected.splice(currentIndex, 1);
        // Remove all subcategories
        subIds.forEach((subId) => {
          const subIndex = newSelected.indexOf(subId);
          if (subIndex !== -1) {
            newSelected.splice(subIndex, 1);
          }
        });
      }
    } else {
      // Toggle subcategory
      if (currentIndex === -1) {
        newSelected.push(categoryId);

        // If all subcategories are now selected, also select the parent
        if (category.parentCategoryId) {
          const parentId = category.parentCategoryId;
          if (
            areAllSubcategoriesSelected(parentId) &&
            !newSelected.includes(parentId)
          ) {
            newSelected.push(parentId);
          }
        }
      } else {
        newSelected.splice(currentIndex, 1);

        // If parent was selected, remove it when any subcategory is deselected
        if (category.parentCategoryId) {
          const parentId = category.parentCategoryId;
          const parentIndex = newSelected.indexOf(parentId);
          if (parentIndex !== -1) {
            newSelected.splice(parentIndex, 1);
          }
        }
      }
    }

    setSelectedCategories(newSelected);
  };

  const handleClearAll = () => {
    setSelectedCategories([]);
  };

  const handleExpandToggle = (categoryId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    // Save both parent categories and subcategories
    // When a parent is selected, we also include all its subcategories
    const categoryIdsToSave: string[] = [];
    const processedSubIds = new Set<string>();

    selectedCategories.forEach((id) => {
      const category = allCategoriesMap.get(id);
      if (!category) return;

      if (category.parentCategoryId) {
        // It's a subcategory - add it
        if (!processedSubIds.has(id)) {
          categoryIdsToSave.push(id);
          processedSubIds.add(id);
        }
      } else {
        // It's a parent category - add all its subcategories
        const subIds = getSubcategoryIds(id);
        if (subIds.length > 0) {
          subIds.forEach((subId) => {
            if (!processedSubIds.has(subId)) {
              categoryIdsToSave.push(subId);
              processedSubIds.add(subId);
            }
          });
        } else {
          // Standalone category (no subcategories) - add itself
          if (!processedSubIds.has(id)) {
            categoryIdsToSave.push(id);
            processedSubIds.add(id);
          }
        }
      }
    });

    onSave(categoryIdsToSave);
    onClose();
  };

  // Use search results when searching, otherwise use flattened categories
  const categoriesToDisplay = useMemo(() => {
    return flattenedCategories;
  }, [flattenedCategories]);

  const renderCategories = (parentId: string | null = null, level = 0) => {
    // In search mode, show categories with their subcategories grouped
    // In normal mode, show hierarchical structure
    const itemsToRender = useSearchResults
      ? categoriesToDisplay.filter((cat) => !cat.parentCategory) // Show parent categories in search
      : parentId === null
        ? categoriesToDisplay.filter((cat) => !cat.parentCategory)
        : categoriesToDisplay.filter(
            (cat) => cat.parentCategory?.id === parentId
          );

    return itemsToRender.map((category) => {
      const hasChildren =
        !useSearchResults &&
        categoriesToDisplay.some((c) => c.parentCategory?.id === category.id);
      const isExpanded = expandedCategories.has(category.id!);
      const isParentCategory = !category.parentCategory;
      const isSelected = selectedCategories.includes(category.id!);
      const allSubsSelected =
        isParentCategory && hasChildren
          ? areAllSubcategoriesSelected(category.id!)
          : false;

      // For search mode, show categories with their subcategories
      if (useSearchResults) {
        const hasSubs =
          category.subcategories && category.subcategories.length > 0;
        return (
          <Box key={category.id} sx={{ mb: 1 }}>
            <ListItem
              disablePadding
              sx={{
                pl: 2,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                borderRadius: 1,
                mb: 0.5,
              }}
            >
              <ListItemButton
                role={undefined}
                onClick={() => handleToggle(category.id!, true)}
                dense
                sx={{
                  borderRadius: 1,
                  py: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={isSelected || allSubsSelected}
                    indeterminate={
                      !isSelected &&
                      !allSubsSelected &&
                      hasSubs &&
                      category.subcategories!.some((sub) =>
                        selectedCategories.includes(sub.id!)
                      )
                    }
                    tabIndex={-1}
                    disableRipple
                    sx={{ p: 0.5 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={category.name || "Unknown"}
                  secondary={
                    hasSubs
                      ? `${category.subcategories!.length} subcategor${category.subcategories!.length > 1 ? "ies" : "y"}`
                      : undefined
                  }
                  primaryTypographyProps={{
                    variant: "body1",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    color: "text.secondary",
                  }}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontWeight: 600,
                      color: "text.primary",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
            {hasSubs && (
              <Box sx={{ pl: 4, pr: 2 }}>
                {category.subcategories!.map((subcat) => (
                  <ListItem
                    key={subcat.id}
                    disablePadding
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      borderRadius: 1,
                      mb: 0.25,
                    }}
                  >
                    <ListItemButton
                      role={undefined}
                      onClick={() => handleToggle(subcat.id!, false)}
                      dense
                      sx={{
                        borderRadius: 1,
                        py: 0.75,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedCategories.includes(subcat.id!)}
                          tabIndex={-1}
                          disableRipple
                          sx={{ p: 0.5 }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={subcat.name || "Unknown"}
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            )}
          </Box>
        );
      }

      // For normal mode, show collapsible dropdowns for parent categories with subcategories
      if (isParentCategory && hasChildren) {
        return (
          <Box key={category.id} sx={{ mb: 0.5 }}>
            <ListItem
              disablePadding
              sx={{
                pl: 2,
                borderRadius: 1,
              }}
            >
              <ListItemButton
                role={undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle(category.id!, true);
                }}
                dense
                sx={{
                  borderRadius: 1,
                  py: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={isSelected || allSubsSelected}
                    indeterminate={
                      !isSelected &&
                      !allSubsSelected &&
                      categoriesToDisplay.some(
                        (c) =>
                          c.parentCategory?.id === category.id &&
                          selectedCategories.includes(c.id!)
                      )
                    }
                    tabIndex={-1}
                    disableRipple
                    sx={{ p: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(category.id!, true);
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={category.name}
                  primaryTypographyProps={{
                    variant: "body1",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontWeight: 600,
                      color: "text.primary",
                    },
                  }}
                />
                <IconButton
                  size="medium"
                  onClick={(e) => handleExpandToggle(category.id!, e)}
                  sx={{
                    ml: 1,
                    color: "text.secondary",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {isExpanded ? (
                    <ExpandLess fontSize="large" />
                  ) : (
                    <ExpandMore fontSize="large" />
                  )}
                </IconButton>
              </ListItemButton>
            </ListItem>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  pl: 4,
                  pr: 2,
                  pt: 0.5,
                  pb: 1,
                }}
              >
                <List dense disablePadding>
                  {renderCategories(category.id, level + 1)}
                </List>
              </Box>
            </Collapse>
          </Box>
        );
      }

      // For parent categories without subcategories or subcategories themselves
      const isSubcategory = !!category.parentCategory;
      return (
        <div key={category.id}>
          <ListItem
            disablePadding
            sx={{
              pl: isMobile ? level * 2 : level > 0 ? 4 : 2,
              "&:hover": {
                backgroundColor: "action.hover",
              },
              borderRadius: 1,
              mb: 0.5,
            }}
          >
            <ListItemButton
              role={undefined}
              onClick={() => handleToggle(category.id!, !isSubcategory)}
              dense
              sx={{
                borderRadius: 1,
                py: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={selectedCategories.includes(category.id!)}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0.5 }}
                />
              </ListItemIcon>
              <ListItemText
                primary={category.name}
                primaryTypographyProps={{
                  variant: "body1",
                  fontWeight: 600,
                  color: "text.primary",
                }}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: 600,
                    color: "text.primary",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </div>
      );
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: isMobile ? "80vh" : "85vh",
          maxHeight: "900px",
          width: isMobile ? "90vw" : "450px",
          maxWidth: "90vw",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
          px: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    edge="end"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
              },
            }}
          />
          {selectedCategories.length > 0 && (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={handleClearAll}
              sx={{
                textTransform: "none",
                color: "text.secondary",
                borderColor: "divider",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: "text.secondary",
                },
                minWidth: "auto",
                px: 1.5,
                py: 0.75,
                fontSize: "0.875rem",
                height: "40px",
              }}
              variant="outlined"
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, flex: 1, overflow: "auto", mt: -3 }}>
        <List dense sx={{ width: "100%" }}>
          {isSearching ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : categoriesToDisplay.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 1, textAlign: "center" }}
            >
              {useSearchResults
                ? "No categories found"
                : "No categories available"}
            </Typography>
          ) : (
            renderCategories()
          )}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" }, // stack vertically on mobile, row on desktop
            justifyContent: "center",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size={isMobile ? "medium" : "small"}
            fullWidth={isMobile}
            sx={{
              minWidth: { xs: "unset", sm: 140 },
              px: { xs: 0, sm: 3 },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            size={isMobile ? "medium" : "small"}
            // disabled={selectedCategories.length === 0}
            fullWidth={isMobile}
            sx={{
              minWidth: { xs: "unset", sm: 180 },
              px: { xs: 0, sm: 4 },
            }}
          >
            Apply Selection
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
